// @ts-strict-ignore
import { useEffect, useMemo, useState } from 'react'
import { orderBy } from 'lodash-es'
import { useGetPaymentMethods } from 'hooks/queries/Beneficiaries/useGetPaymentMethods'
import { useBeneficiariesQuery } from 'hooks/queries/useBeneficiariesQuery'
import {
  BeneficiaryDetails,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  BeneficiaryType,
} from 'types/beneficiaries'
import { mapPaymentMethodToBenficiaryDetails } from 'utils/beneficiaries'

interface WalletCapabilities {
  supportsWithdrawals: boolean
  supportsPay: boolean
  isB2cPayoutEnabled: boolean
}

export interface BeneficiaryFilters {
  beneficiaryType?: BeneficiaryType
  currency?: string
  entityType?: BeneficiaryEntityType
  transferDestinations?: BeneficiaryTransferDestination[]
  beneficiarySearchTerm?: string
}

interface GetBeneficiariesProps {
  filters: BeneficiaryFilters
  capabilities: WalletCapabilities
}

// Helper functions for filtering
const matchesCurrency = (beneficiary: BeneficiaryDetails, currency?: string): boolean => {
  if (!currency) return true
  return beneficiary?.currency === currency
}

const matchesTransferDestination = (
  beneficiary: BeneficiaryDetails,
  transferDestinations?: BeneficiaryTransferDestination[]
): boolean => {
  if (!transferDestinations?.length) return true
  return transferDestinations.includes(beneficiary?.transferDestination)
}

const matchesSearchTerm = (beneficiary: BeneficiaryDetails, searchTerm?: string): boolean => {
  if (!searchTerm) return true

  const normalizedSearchTerm = searchTerm.toLowerCase()
  const searchableFields = [
    beneficiary?.businessName,
    beneficiary?.firstName,
    beneficiary?.lastName,
    beneficiary?.alias,
  ].filter(Boolean)

  return searchableFields.some(field => field.toLowerCase().includes(normalizedSearchTerm))
}

const matchesEntityType = (
  beneficiary: BeneficiaryDetails,
  entityType?: BeneficiaryEntityType,
  isB2cPayoutEnabled?: boolean
): boolean => {
  if (entityType) {
    return beneficiary?.entityType === entityType
  }

  // If B2C payouts are not enabled, exclude individual beneficiaries
  if (!isB2cPayoutEnabled && beneficiary?.entityType === BeneficiaryEntityType.INDIVIDUAL) {
    return false
  }

  return true
}

const matchesBeneficiaryType = (
  beneficiary: BeneficiaryDetails,
  beneficiaryType?: BeneficiaryType,
  capabilities?: WalletCapabilities
): boolean => {
  const { supportsWithdrawals, supportsPay } = capabilities || {}

  if (beneficiaryType) {
    return beneficiary?.beneficiaryType === beneficiaryType
  }

  // If wallet supports neither withdrawals nor pay, show no beneficiaries
  if (!supportsWithdrawals && !supportsPay) return false

  // If wallet only supports pay, exclude first-party beneficiaries
  if (
    !supportsWithdrawals &&
    supportsPay &&
    beneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY
  ) {
    return false
  }

  // If wallet only supports withdrawals, exclude third-party beneficiaries
  if (
    supportsWithdrawals &&
    !supportsPay &&
    beneficiary?.beneficiaryType === BeneficiaryType.THIRD_PARTY
  ) {
    return false
  }

  return true
}

const filterBeneficiaries = (
  beneficiaries: BeneficiaryDetails[],
  filters: BeneficiaryFilters,
  capabilities: WalletCapabilities
): BeneficiaryDetails[] => {
  const { currency, entityType, transferDestinations, beneficiarySearchTerm, beneficiaryType } =
    filters

  return beneficiaries.filter(beneficiary => {
    return (
      matchesCurrency(beneficiary, currency) &&
      matchesTransferDestination(beneficiary, transferDestinations) &&
      matchesSearchTerm(beneficiary, beneficiarySearchTerm) &&
      matchesEntityType(beneficiary, entityType, capabilities?.isB2cPayoutEnabled) &&
      matchesBeneficiaryType(beneficiary, beneficiaryType, capabilities)
    )
  })
}

export const useGetBeneficiaries = ({ filters, capabilities }: GetBeneficiariesProps) => {
  const { currency, beneficiaryType } = filters || {}
  const [fiatBeneficiaries, setFiatBeneficiaries] = useState<BeneficiaryDetails[] | undefined>()
  const [isLoading, setIsLoading] = useState(false)

  const { isError: isFetchingPaymentMethodsError, refetch: refetchPaymentMethods } =
    useGetPaymentMethods({ enabled: false })

  const { isError: isFetchingBeneficiariesError, refetch: refetchBeneficiaries } =
    useBeneficiariesQuery(
      {
        sort: 'businessName',
        size: 1000,
        beneficiaryType,
        currency,
      },
      false
    )

  const getBeneficiaries = async ({ filters, capabilities }: GetBeneficiariesProps) => {
    setIsLoading(true)
    const paymentMethodsData = await refetchPaymentMethods()
    const beneficiariesData = await refetchBeneficiaries()
    const paymentMethods = paymentMethodsData?.data
    const beneficiaries = beneficiariesData?.data?.content

    let legacyPaymentMethodBeneficiaries = []

    if (paymentMethods?.length && beneficiaries?.length) {
      legacyPaymentMethodBeneficiaries = paymentMethods?.reduce(
        (legacyBeneficiaries, currentPaymentMethod) => {
          const matchingBeneficiary = beneficiaries?.find(
            beneficiary => beneficiary?.reference === currentPaymentMethod?.reference
          )

          if (!matchingBeneficiary) {
            const benefiary = mapPaymentMethodToBenficiaryDetails(currentPaymentMethod)
            legacyBeneficiaries?.push(benefiary)
          }

          return legacyBeneficiaries
        },
        []
      )
    }

    const allBeneficiaries: BeneficiaryDetails[] = orderBy(
      [...legacyPaymentMethodBeneficiaries, ...beneficiaries],
      ['businessName', 'firstName', 'lastName', 'alias'],
      ['asc', 'asc', 'asc', 'asc']
    )

    const filteredBeneficiaries = filterBeneficiaries(allBeneficiaries, filters, capabilities)

    setFiatBeneficiaries(filteredBeneficiaries)
    setIsLoading(false)
  }

  const fetchBeneficiaries = async ({ filters, capabilities }: GetBeneficiariesProps) => {
    return getBeneficiaries({ filters, capabilities })
  }

  useEffect(() => {
    if (filters) {
      fetchBeneficiaries({ filters, capabilities })
    }
  }, [filters, capabilities])

  return useMemo(() => {
    return {
      fiatBeneficiaries,
      isFetching: isLoading,
      isError: isFetchingPaymentMethodsError || isFetchingBeneficiariesError,
    }
  }, [fiatBeneficiaries, isLoading, isFetchingPaymentMethodsError, isFetchingBeneficiariesError])
}
